import React, { Dispatch, SetStateAction } from 'react';
import { SingleDatePicker } from 'react-dates';
import moment from 'moment-timezone';
import { GqlCollection } from 'types/graphql';
import { CheckboxOld, Button } from 'shared/components';
import { TIME_FORMAT } from 'src/utils/time-utils';
import { DayRow } from './day-row';
import { Container, SectionName, ScheduleFormRow, DatePicker, StyledSelect, NoEndDate, DaysContainer } from './styles';
import { Day } from './utils';
import { useScheduleForm } from './use-schedule-form';

type ScheduleFormProps = {
  collection?: GqlCollection;
  currentStepIndex: number;
  setStepNumber: (newStep: number) => void;
  setCollectionData: ({ schedule }: Pick<GqlCollection, 'schedule'>) => void;
  setIsDirty: Dispatch<SetStateAction<boolean>>;
};

export function ScheduleForm({
  collection,
  currentStepIndex,
  setCollectionData,
  setStepNumber,
  setIsDirty,
}: ScheduleFormProps): JSX.Element {
  const {
    startDate,
    endDate,
    startTime,
    endTime,
    noEndDate,
    days,
    startDayVisible,
    endDayVisible,
    times,
    setNoEndDate,
    setStartDayVisible,
    setEndDayVisible,
    handleUpdateDay,
    handleUpdateStartTime,
    handleUpdateEndTime,
    handleUpdateStartDate,
    handleUpdateEndDate,
    handleCopyScheduleToAll,
    handleToggleAllDay,
    validateData,
    dispensaryTimezone,
  } = useScheduleForm({
    collection,
    currentStepIndex,
    setStepNumber,
    setCollectionData,
    setIsDirty,
  });

  return (
    <Container>
      <div>
        <SectionName>Start Date:</SectionName>
        <ScheduleFormRow>
          <DatePicker>
            <SingleDatePicker
              date={startDate.clone().startOf('day')}
              daySize={32}
              focused={startDayVisible}
              hideKeyboardShortcutsPanel
              id='add-collection-start-day-picker'
              noBorder
              numberOfMonths={1}
              onDateChange={(date) => handleUpdateStartDate(date as moment.Moment)}
              onFocusChange={({ focused }) => setStartDayVisible(focused)}
              placeholder=''
            />
          </DatePicker>
          <StyledSelect
            height={45}
            fontSize='13px'
            value={startTime}
            name='startTime'
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleUpdateStartTime(e.target.value)}
            timezone={`(${moment.tz(dispensaryTimezone).zoneAbbr()})`}
          >
            {times.map((time: string) => (
              <option key={time} value={time}>
                {moment(time, TIME_FORMAT).format('h:mm A')}
              </option>
            ))}
          </StyledSelect>
        </ScheduleFormRow>
      </div>

      <div>
        <SectionName>End Date:</SectionName>
        <ScheduleFormRow>
          <DatePicker disabled={noEndDate}>
            <SingleDatePicker
              date={noEndDate ? null : endDate.clone().startOf('day')}
              disabled={noEndDate}
              daySize={32}
              focused={endDayVisible}
              hideKeyboardShortcutsPanel
              id='add-collection-end-day-picker'
              noBorder
              numberOfMonths={1}
              onDateChange={(date) => handleUpdateEndDate(date as moment.Moment)}
              onFocusChange={({ focused }) => setEndDayVisible(focused)}
              placeholder={noEndDate ? 'Date N/A' : ''}
            />
          </DatePicker>
          <StyledSelect
            height={45}
            fontSize='13px'
            value={endTime}
            name='endTime'
            disabled={noEndDate}
            timezone={!noEndDate && `(${moment.tz(dispensaryTimezone).zoneAbbr()})`}
            onChange={(e: React.ChangeEvent<HTMLSelectElement>) => handleUpdateEndTime(e.target.value)}
          >
            {noEndDate ? (
              <option value=''>Time N/A</option>
            ) : (
              times.map((time: string) => (
                <option key={time} value={time}>
                  {moment(time, TIME_FORMAT).format('h:mm A')}
                </option>
              ))
            )}
          </StyledSelect>
        </ScheduleFormRow>
        <NoEndDate>
          <CheckboxOld checked={noEndDate} onChange={() => setNoEndDate(!noEndDate)} />
          <span>No End Date</span>
        </NoEndDate>
      </div>

      <div>
        <SectionName>Days And Hours:</SectionName>
        <DaysContainer>
          {days.map((day: Day) => (
            <DayRow
              key={day.key}
              day={day}
              dispensaryTimezone={dispensaryTimezone}
              times={times}
              handleCopyScheduleToAll={handleCopyScheduleToAll}
              handleToggleAllDay={handleToggleAllDay}
              handleUpdateDay={handleUpdateDay}
            />
          ))}
        </DaysContainer>
      </div>

      <Button width='115px' onClick={validateData}>
        Continue
      </Button>
    </Container>
  );
}
